import { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { get } from '../utils/api';
import { RushLeaderboardEntry, RushLeaderboardResponse } from '../types/triviaRush';

const PAGE_SIZE = 100;

interface UseRushLeaderboardOptions {
  enabled?: boolean;
}

export function useRushLeaderboard({ enabled = true }: UseRushLeaderboardOptions = {}) {
  const [allEntries, setAllEntries] = useState<RushLeaderboardEntry[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['rush-leaderboard'],
    queryFn: async () => {
      const res = await get<RushLeaderboardResponse>(
        `/api/trivia-rush/leaderboard?limit=${PAGE_SIZE}&offset=0`
      );
      return res.data!;
    },
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    enabled,
    refetchOnWindowFocus: false,
  });

  // Sync state from React Query cache (fixes mobile remount showing empty data)
  useEffect(() => {
    if (data?.leaderboard) {
      setAllEntries(data.leaderboard);
      setOffset(PAGE_SIZE);
      setHasMore(data.leaderboard.filter(e => !e.is_current_user || e.rank <= PAGE_SIZE).length >= PAGE_SIZE);
    }
  }, [data]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const res = await get<RushLeaderboardResponse>(
        `/api/trivia-rush/leaderboard?limit=${PAGE_SIZE}&offset=${offset}`
      );
      const resp = res.data!;
      // Filter out the appended current-user row and any duplicates
      const newEntries = resp.leaderboard.filter(
        e => !allEntries.some(ex => ex.user_id === e.user_id)
      );
      setAllEntries(prev => [...prev, ...newEntries]);
      setOffset(prev => prev + PAGE_SIZE);
      setHasMore(newEntries.length >= PAGE_SIZE);
    } catch {
      // Silently fail — user can retry
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, offset, allEntries]);

  return {
    entries: allEntries,
    currentUser: data?.currentUser ?? null,
    isLoading,
    hasMore,
    loadMore,
    loadingMore,
    refetch,
  };
}
